'use client';

import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Info, Locate, Route, Search, Shield, Sparkles, User, Zap } from 'lucide-react';
import { fetchAuthMe, searchQuery } from '@/lib/api';
import type { SearchHit } from '@/types/charger';

export default function TopBar({
  activeCount,
  onFlyTo,
  onPickOperator,
  onPickCharger,
  onSearchOpen,
  onOpenInfo,
  onOpenGuide,
  onOpenRoutePlanner,
}: {
  activeCount: number;
  onFlyTo: (lng: number, lat: number, zoom?: number) => void;
  onPickOperator: (code: string) => void;
  onPickCharger: (id: number, lng: number, lat: number) => void;
  onSearchOpen?: (open: boolean) => void;
  onOpenInfo?: () => void;
  onOpenGuide?: () => void;
  onOpenRoutePlanner?: () => void;
}) {
  const [q, setQ] = useState('');
  const [hits, setHits] = useState<SearchHit[]>([]);
  const [open, setOpen] = useState(false);
  const [me, setMe] = useState<{ name?: string } | null | undefined>(undefined);
  const [panel, setPanel] = useState<{ top: number; left: number; width: number } | null>(null);
  const box = useRef<HTMLDivElement>(null);

  useEffect(() => {
    fetchAuthMe().then((u) => setMe(u));
  }, []);

  useEffect(() => {
    if (q.trim().length < 2) {
      setHits([]);
      setOpen(false);
      onSearchOpen?.(false);
      return;
    }
    const t = setTimeout(() => {
      searchQuery(q.trim()).then((d) => {
        setHits(d);
        const shown = d.length > 0;
        setOpen(shown);
        onSearchOpen?.(shown);
      });
    }, 220);
    return () => clearTimeout(t);
  }, [q, onSearchOpen]);

  useLayoutEffect(() => {
    if (!open || !box.current) {
      setPanel(null);
      return;
    }
    const place = () => {
      const el = box.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      const mobile = window.innerWidth < 768;
      const gutter = 8;
      setPanel(
        mobile
          ? { top: r.bottom + 4, left: gutter, width: Math.max(0, window.innerWidth - gutter * 2) }
          : { top: r.bottom + 4, left: r.left, width: r.width }
      );
    };
    place();
    window.addEventListener('resize', place);
    window.addEventListener('orientationchange', place);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('orientationchange', place);
    };
  }, [open, hits.length]);

  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (box.current && !box.current.contains(e.target as Node)) {
        setOpen(false);
        onSearchOpen?.(false);
      }
    };
    document.addEventListener('click', onDoc);
    return () => document.removeEventListener('click', onDoc);
  }, [onSearchOpen]);

  const locate = () => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition((pos) => {
      onFlyTo(pos.coords.longitude, pos.coords.latitude, 14);
    });
  };

  return (
    <header className="pointer-events-auto relative z-50 flex items-center gap-3 w-full overflow-visible bg-[#102016]/95 px-4 py-2.5 text-emerald-50 shadow-xl backdrop-blur-md border-b border-white/10 md:px-6">
      <a href="/" aria-label="evtolto.app – főoldal" className="flex shrink-0 items-center gap-2 font-semibold tracking-tight">
        <span className="flex h-8 w-8 items-center justify-center rounded-full bg-brand text-white">
          <Zap className="h-4 w-4 fill-current" aria-hidden="true" />
        </span>
        <span className="hidden sm:inline" aria-hidden="true">evtolto.app</span>
      </a>
      <span className="hidden rounded-full bg-white/10 px-2 py-0.5 text-[11px] font-medium text-emerald-100 md:inline">
        {activeCount.toLocaleString('hu-HU')}+ hazai töltőpont
      </span>
      <nav className="hidden items-center gap-2 text-[11px] font-semibold text-emerald-100 xl:flex">
        <a href="/utmutato" className="hover:text-white">
          Útmutatók
        </a>
        <a href="/toltok" className="hover:text-white">
          Városok
        </a>
        <a href="/gyik" className="hover:text-white">
          GYIK
        </a>
      </nav>
      <div className="relative min-w-0 flex-1" ref={box}>
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" aria-hidden="true" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onFocus={() => {
            if (hits.length) {
              setOpen(true);
              onSearchOpen?.(true);
            }
          }}
          aria-label="Keresés: város, cím vagy CPO üzemeltető"
          placeholder="Keresés: város, cím, CPO…"
          className="w-full rounded-full border-0 bg-white py-2 pl-9 pr-3 text-sm text-slate-900 outline-none ring-0 placeholder:text-slate-400"
        />
        {open && hits.length > 0 && panel && (
          <ul
            className="fixed z-[80] max-h-[min(70dvh,24rem)] overflow-auto rounded-xl bg-white py-1 text-sm text-slate-800 shadow-xl"
            style={{ top: panel.top, left: panel.left, width: panel.width }}
          >
            {hits.map((h, i) => (
              <li key={i}>
                <button
                  className="flex w-full items-start gap-2 px-3 py-2 text-left hover:bg-emerald-50"
                  onClick={() => {
                    setOpen(false);
                    onSearchOpen?.(false);
                    setQ(h.label);
                    if (h.type === 'operator' && h.operator) onPickOperator(h.operator);
                    if (h.type === 'charger' && h.id && h.lng != null && h.lat != null) {
                      onPickCharger(h.id, h.lng, h.lat);
                    }
                    if (h.type === 'place' && h.lng != null && h.lat != null) onFlyTo(h.lng, h.lat, 12);
                  }}
                >
                  <span className="mt-0.5 shrink-0 rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase leading-none text-slate-500">
                    {h.nearby ? 'közeli' : h.type === 'place' ? 'hely' : h.type === 'operator' ? 'CPO' : 'töltő'}
                  </span>
                  <span className="min-w-0 flex-1 whitespace-normal break-words leading-snug">{h.label}</span>
                  {h.distance ? (
                    <span className="shrink-0 text-xs font-medium text-emerald-700">{h.distance}</span>
                  ) : null}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
      <button
        type="button"
        onClick={onOpenInfo}
        className="flex items-center gap-1 rounded-full bg-white/10 px-2.5 py-2 text-xs font-semibold text-emerald-200 transition hover:bg-white/20 hover:text-white"
        title="A térképről"
        aria-label="A térképről"
      >
        <Info className="h-4 w-4 text-emerald-400" aria-hidden="true" />
        <span className="hidden md:inline">A térképről</span>
      </button>
      <button
        type="button"
        onClick={() => {
          if (!me) {
            window.location.href = '/account/login.php?next=/';
            return;
          }
          onOpenRoutePlanner?.();
        }}
        className="flex items-center gap-1 rounded-full bg-white/10 px-2.5 py-2 text-xs font-semibold text-emerald-200 hover:bg-white/20 hover:text-white transition"
        title="Útvonaltervező"
        aria-label="Útvonaltervező"
      >
        <Route className="h-4 w-4 text-emerald-400" aria-hidden="true" />
        <span className="hidden md:inline">Útvonaltervező</span>
      </button>
      <button
        type="button"
        onClick={onOpenGuide}
        className="flex items-center gap-1 rounded-full bg-white/10 px-2.5 py-2 text-xs font-semibold text-emerald-200 hover:bg-white/20 hover:text-white transition"
        title="Funkciók – használati útmutató"
        aria-label="Funkciók – használati útmutató"
      >
        <Sparkles className="h-4 w-4 text-emerald-400" aria-hidden="true" />
        <span className="hidden md:inline">Funkciók</span>
      </button>
      <button
        type="button"
        onClick={locate}
        className="hidden rounded-full bg-white/10 p-2 hover:bg-white/20 sm:block"
        title="Saját helyzet"
        aria-label="Saját helyzet meghatározása"
      >
        <Locate className="h-4 w-4" aria-hidden="true" />
      </button>
      <a
        href="/adatkezelesi-tajekoztato"
        className="hidden rounded-full bg-white/10 p-2 hover:bg-white/20 md:block"
        title="Adatkezelési tájékoztató"
        aria-label="Adatkezelési tájékoztató"
      >
        <Shield className="h-4 w-4" aria-hidden="true" />
      </a>
      <a
        href={me ? '/account/' : '/account/login.php'}
        className="flex items-center gap-1 rounded-full bg-emerald-700 px-3 py-2 text-xs font-semibold text-white hover:bg-emerald-800 transition"
        aria-label={me ? 'Profil – fiók megnyitása' : 'Belépés – bejelentkezés fiókba'}
      >
        <User className="h-4 w-4" aria-hidden="true" />
        <span className="hidden sm:inline">{me ? 'Profil' : 'Belépés'}</span>
      </a>
    </header>
  );
}
